import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import type { TaxonomyListColumn } from '@/entities/taxonomy-list';
import { resolveBoundColumnPair } from '@inference.engine/taxonomy/web';
import { FormField } from '@/shared/ui/FormDialog';
import { FormSheet } from '@/shared/ui/FormSheet';

export interface FillEmptyCellsModalProps {
  columns: TaxonomyListColumn[];
  onClose: () => void;
  /** Fills the blank cells of `targetColumnIds` from `sourceColumnId`. */
  onApply: (sourceColumnId: string, targetColumnIds: string[]) => void;
}

function othersThan(columns: TaxonomyListColumn[], sourceId: string | undefined) {
  return new Set(columns.filter((c) => c.id !== sourceId).map((c) => c.id));
}

/**
 * Copies one column into the empty cells of the columns the user ticks.
 *
 * The source starts on the column a binding would display, which is not always the first one: a
 * list laid out `Code | Length` keeps its values in `Length`.
 */
export function FillEmptyCellsModal({ columns, onClose, onApply }: FillEmptyCellsModalProps) {
  const { t } = useTranslation('knowledge');
  const sourceSelectId = useId();
  const [sourceId, setSourceId] = useState(
    () => (resolveBoundColumnPair(columns)?.valueColumn ?? columns.at(0))?.id,
  );
  const [selected, setSelected] = useState<Set<string>>(() => othersThan(columns, sourceId));
  const targets = columns.filter((c) => c.id !== sourceId);

  const chooseSource = (next: string) => {
    setSourceId(next);
    setSelected(othersThan(columns, next));
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <FormSheet
      className="w-[28rem]"
      open
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.fillEmptyCellsModal.title')}
    >
      <FormSheet.Body className="flex flex-col gap-3">
        <FormField label={t('taxonomy.fillEmptyCellsModal.copyFrom')} htmlFor={sourceSelectId}>
          <Select value={sourceId} onValueChange={chooseSource}>
            <SelectTrigger id={sourceSelectId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {columns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <p className="text-xs text-muted-foreground">
          {t('taxonomy.fillEmptyCellsModal.explainer', {
            source: columns.find((c) => c.id === sourceId)?.name ?? '',
          })}
        </p>
        <ul className="flex flex-col gap-2">
          {targets.map((c) => (
            <li key={c.id}>
              <label className="flex cursor-pointer items-center gap-2.5">
                <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                <span className="text-sm text-foreground">{c.name}</span>
              </label>
            </li>
          ))}
        </ul>
      </FormSheet.Body>
      <FormSheet.Footer
        primary={{
          label: t('taxonomy.fillEmptyCellsModal.fill'),
          onClick: () =>
            sourceId &&
            onApply(
              sourceId,
              targets.filter((c) => selected.has(c.id)).map((c) => c.id),
            ),
          disabled: !sourceId || selected.size === 0,
        }}
        secondary={{ label: t('taxonomy.fillEmptyCellsModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}
